import { useMemo, useState } from 'react';
import { Add, Refresh, EditOutlined, DeleteOutline } from '@mui/icons-material';
import { toast } from 'sonner';
import {
  Button,
  DataTable,
  SearchBox,
  SegmentedTabs,
  Pagination,
  ActionMenu,
  ConfirmDialog,
  MetricPill,
} from 'Components/ui';
import type { ActionMenuOption, DataTableRow, PaginationLabels } from 'Components/ui';
import { PageBody, Header, Subtitle, Toolbar, ToolbarEnd } from 'Components/ui/layout';
import SelectCoursesDialog from 'Components/SelectCoursesDialog';
import CoursePriorityTag from 'Components/CoursePriorityTag/CoursePriorityTag';
import CourseStatusChip from 'Components/CourseStatusChip';
import { COURSE_STATUS_APPROVED } from 'Constants/General';
import useTranslation from 'Hooks/useTranslation';
import { usePermissionStore } from 'stores';
import employeesApi from 'Api/axios/employees-api';
import type { AssignableCourseI, DevelopmentPlanEntryI, EmployeeProfileI } from 'types';
import useEmployeeDevelopmentPlan from './Hooks/useEmployeeDevelopmentPlan';
import useDevelopmentPlanTable from './Hooks/useDevelopmentPlanTable';
import useAssignableCourses from './Hooks/useAssignableCourses';
import useAvailableOptionalCourses from './Hooks/useAvailableOptionalCourses';
import BulkApproveDrawer from './Components/BulkApproveDrawer';

interface Props {
  profile: EmployeeProfileI | null;
  isAdmin: boolean;
}

const hasPermission = (permissions: Array<{ key: string; assigned: boolean }>, lookup: string) =>
  permissions.some(permission => permission.key === lookup && permission.assigned === true);

/**
 * Pestaña "Plan de desarrollo".
 *
 * Antes eran cuatro tablas apiladas alimentadas por dos peticiones distintas
 * —tres buckets del plan y una lista aparte de cursos asignados—. Ahora es una
 * tabla con cinco pestañas sobre la única respuesta del backend, que ya trae
 * cada curso en un solo bucket.
 *
 * Efecto visible del cambio: los cursos en borrador dejan de aparecer, y un
 * curso opcional asignado a mano se muestra en "Opcionales" y no en "Asignados".
 */
const DevelopmentPlan = ({ profile }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');
  const permissions = usePermissionStore(state => state.permissions);

  const canApprove = hasPermission(permissions, 'employee_development.*');
  const canManageCourses = hasPermission(permissions, 'user_course.*');

  const {
    developmentPlan,
    selectedCourse,
    setSelectedCourse,
    openApprove,
    toggleApprove,
    openResetProgress,
    toggleResetProgress,
    openCourseAssign,
    toggleCourseAssigner,
    openDeassignConfirmation,
    toggleDeassignConfirmation,
    assignCourses,
    unassignSelectedCourse,
    updateUserCourse,
    approveUserCourses,
  } = useEmployeeDevelopmentPlan({ userId: profile?.id ?? '' });

  const assignable = useAssignableCourses(profile?.id ?? '');
  const available = useAvailableOptionalCourses(profile?.id ?? '');

  const table = useDevelopmentPlanTable({
    buckets: developmentPlan,
    availableOptional: available.allCourses,
  });

  // Curso disponible cuya inscripción se está creando, para no ofrecer dos veces
  // la misma acción mientras la petición está en vuelo.
  const [enrolling, setEnrolling] = useState<string | null>(null);

  // Un solo objeto de etiquetas para la tabla y para el diálogo de asignación.
  const paginationLabels: PaginationLabels = {
    showing: t2('pagination_showing') as string,
    of: t2('pagination_of') as string,
    page: t2('pagination_page') as string,
    prev: t2('pagination_prev') as string,
    next: t2('pagination_next') as string,
    perPage: t2('pagination_per_page') as string,
  };

  const columns = useMemo(
    () => [
      { key: 'name', label: t2('course_name'), width: '1.8fr', strong: true },
      { key: 'duration', label: t2('time'), width: '0.7fr' },
      {
        key: 'priority',
        label: t2('priority'),
        width: '1fr',
        render: (row: DataTableRow) => {
          const entry = row.entry as DevelopmentPlanEntryI | null;
          const priority = entry ? entry.Course.Priority : (row.available as AssignableCourseI).priority;
          return priority ? <CoursePriorityTag id={priority.id} name={priority.name} /> : null;
        },
      },
      {
        key: 'status',
        label: t2('status'),
        width: '0.9fr',
        render: (row: DataTableRow) => {
          const entry = row.entry as DevelopmentPlanEntryI | null;

          // Sin inscripción no hay estatus que pintar: el curso está a su
          // alcance pero nunca lo abrió. El identificador vacío cae en el gris
          // por omisión de `CourseStatusChip`.
          if (!entry) return <CourseStatusChip statusId="" status={t2('status_not_started') as string} />;

          return <CourseStatusChip statusId={entry.UserCourseStatus.id} status={entry.UserCourseStatus.name} />;
        },
      },
    ],
    [t2]
  );

  const rows = useMemo(
    () =>
      table.rows.map(row => ({
        id: row.id,
        name: row.name,
        duration: row.duration ?? t2('not_available'),
        entry: row.entry,
        available: row.available,
      })),
    [table.rows, t2]
  );

  /**
   * Aprueba un opcional que el empleado nunca abrió.
   *
   * Sin inscripción no hay nada que aprobar, así que primero se materializa la
   * fila —el backend ya comprueba que tenga derecho al curso— y con la entrada
   * que devuelve se abre el cajón de siempre, que no se entera de nada.
   */
  const approveAvailable = async (course: AssignableCourseI) => {
    setEnrolling(course.id);
    const response = await employeesApi.enrollOptionalCourse(profile?.id ?? '', course.id);
    setEnrolling(null);

    if (response.error || !response.data) {
      toast.error(response.error ?? (t2('enroll_error') as string));
      return;
    }

    available.refetch();
    setSelectedCourse(response.data);
    toggleApprove(true);
  };

  const rowActions = (row: DataTableRow) => {
    const available_ = row.available as AssignableCourseI | null;

    // Un curso disponible solo admite una acción: acreditarlo. Reiniciar el
    // progreso o desasignarlo no significan nada sin inscripción.
    if (available_) {
      if (!canApprove) return <ActionMenu label={t2('actions')} options={[]} />;

      return (
        <ActionMenu
          label={t2('actions')}
          options={[
            {
              label: t2('approve') as string,
              icon: <EditOutlined />,
              onSelect: () => approveAvailable(available_),
            },
          ]}
        />
      );
    }

    const entry = row.entry as DevelopmentPlanEntryI;
    const options: ActionMenuOption[] = [];

    if (canApprove && entry.UserCourseStatus.id !== COURSE_STATUS_APPROVED) {
      options.push({
        label: t2('approve'),
        icon: <EditOutlined />,
        onSelect: () => {
          setSelectedCourse(entry);
          toggleApprove(true);
        },
      });
    }

    if (canApprove) {
      options.push({
        label: t2('reset_progress'),
        icon: <Refresh />,
        onSelect: () => {
          setSelectedCourse(entry);
          toggleResetProgress(true);
        },
      });
    }

    // Desasignar solo tiene sentido en lo que se asignó a mano: el backend
    // rechaza lo demás, y ofrecer la opción sería prometer algo que falla.
    if (canManageCourses && entry.origin === 'manual') {
      options.push({
        label: t2('deassign_modal.title'),
        icon: <DeleteOutline />,
        tone: 'danger',
        onSelect: () => {
          setSelectedCourse(entry);
          toggleDeassignConfirmation(true);
        },
      });
    }

    return <ActionMenu label={t2('actions')} options={options} />;
  };

  return (
    <PageBody>
      <Header>
        {/* Sin título: la pestaña de arriba ya dice "Plan de desarrollo". Se
            queda el nombre, que no aparece en ningún otro sitio de esta
            pestaña. El total vive junto a las pestañas, en la fila de la
            tabla, que es donde se compara con los conteos de cada origen. */}
        <div>
          <Subtitle>{profile?.fullName}</Subtitle>
        </div>
        {canManageCourses ? (
          <Button type="button" variant="primary" onClick={() => toggleCourseAssigner(true)}>
            <Add />
            {t2('assign_courses')}
          </Button>
        ) : null}
      </Header>

      <Toolbar>
        <SearchBox value={table.search} placeholder={t2('search_course')} onChange={table.setSearch} />
        <ToolbarEnd>
          {/* Fuera del control segmentado a propósito: dentro parecería una
              pestaña más y se esperaría que filtrara la tabla. */}
          <MetricPill label={t2('total_courses_label') as string} value={table.totalCourses} />

          {/* Los contadores cuentan filas: en "Opcionales" incluyen los cursos
              a su alcance que todavía no ha iniciado. */}
          <SegmentedTabs
            options={table.tabs.map(tab => ({ ...tab, label: t2(`origin_${tab.value}`) as string }))}
            value={table.origin}
            onChange={table.setOrigin}
          />
        </ToolbarEnd>
      </Toolbar>

      <DataTable
        rows={rows}
        columns={columns}
        labels={{ empty: t2('empty_plan') as string, viewDetails: t2('SeeDetails') as string }}
        rowActions={enrolling ? undefined : canApprove || canManageCourses ? rowActions : undefined}
      />

      <Pagination
        page={table.page}
        pageSize={table.pageSize}
        totalPages={table.totalPages}
        totalItems={table.totalItems}
        labels={paginationLabels}
        onChange={table.setPage}
        onPageSizeChange={table.setPageSize}
      />

      <SelectCoursesDialog
        open={openCourseAssign}
        labels={{
          title: t2('assign_courses') as string,
          searchPlaceholder: t2('search_course') as string,
          name: t2('course_name') as string,
          duration: t2('time') as string,
          selectAll: t2('select_all') as string,
          // El diálogo pinta el conteo y luego esta etiqueta, así que va sin
          // marcador. `selected_count` es la frase de EmployeeSelectionPanel,
          // que sí sustituye `{n}` por su cuenta.
          selectedCount: t2('selected_courses_count') as string,
          empty: t2('no_courses_to_assign') as string,
          loading: t2('loading') as string,
          cancel: t2('Cancel') as string,
          confirm: t2('assign_courses') as string,
          pagination: paginationLabels,
        }}
        courses={assignable.courses}
        loading={assignable.loading}
        search={assignable.search}
        onSearchChange={assignable.setSearch}
        page={assignable.page}
        pageSize={assignable.pageSize}
        totalPages={assignable.totalPages}
        totalItems={assignable.totalItems}
        onPageChange={assignable.setPage}
        onPageSizeChange={assignable.setPageSize}
        onClose={() => toggleCourseAssigner(false)}
        onConfirm={async courseIds => {
          const ok = await assignCourses(courseIds);
          if (ok) assignable.refetch();
          return ok;
        }}
      />

      <ConfirmDialog
        open={openDeassignConfirmation}
        tone="danger"
        title={t2('deassign_modal.title') as string}
        description={t2('deassign_modal.content') as string}
        cancelLabel={t2('deassign_modal.cancel') as string}
        confirmLabel={t2('deassign_modal.accept') as string}
        onCancel={() => toggleDeassignConfirmation(false)}
        onConfirm={unassignSelectedCourse}
      />

      <ConfirmDialog
        open={openResetProgress}
        tone="danger"
        icon={<Refresh />}
        title={t2('reset_progress_modal.title') as string}
        description={t2('reset_progress_modal.content') as string}
        cancelLabel={t2('reset_progress_modal.cancel') as string}
        confirmLabel={t2('reset_progress_modal.accept') as string}
        onCancel={() => toggleResetProgress(false)}
        onConfirm={() => updateUserCourse('reset_progress')}
      />

      <BulkApproveDrawer
        open={openApprove}
        course={selectedCourse}
        onClose={() => toggleApprove(false)}
        onApprove={approveUserCourses}
      />
    </PageBody>
  );
};

export default DevelopmentPlan;
